import React, { useEffect, useState } from 'react';

import Icon from 'dpl/common/components/Icon';
import Banner from 'dpl/common/design_system/Banner';
import Button from 'dpl/common/design_system/Button';
import Toggle from 'dpl/common/design_system/Toggle';
import MiniOverlayModal from 'dpl/common/mini_overlay_modal/components/MiniOverlayModal';
import { PRIVACY_PATH } from 'dpl/shared/constants/urls';
import { ANALYTICS_EVENTS } from 'dpl/constants/analytics';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  isAdTrackingOptedOut,
  isGPCEnabled,
  setAdTrackingOptOut
} from 'dpl/util/adPreferences';

// Identifies where an opt-out / opt-in event originated for analytics.
const PRIVACY_CHOICES_SOURCE = 'privacy_choices_modal';

// Subtitle shown next to the toggle. GPC takes precedence (locked off),
// otherwise it reflects the sharing choice.
const GPC_SUBTITLE = "Controlled by your browser's Global Privacy Control.";
const SHARING_SUBTITLE = 'Share my activity to personalize ads.';

interface PrivacyChoicesModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function SavedConfirmation({
  optedOut,
  onClose
}: {
  optedOut: boolean;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-column items-center tc pt10 ph8 pb8 gap-20">
      <div className="flex items-center justify-center br-100 pa4 bg-teal-light">
        <Icon
          className="green-500"
          name="checkmark"
          height="28px"
          width="28px"
        />
      </div>
      <h2 className="font-serif font-20 fw-medium lh-title">
        Your preference has been saved.
      </h2>
      {optedOut && (
        <p className="secondary font-16 lh-body">
          We won&apos;t share your information with advertising partners from
          this browser unless you change your setting.
        </p>
      )}
      <Button
        variant="primary"
        size="default"
        className="w-100"
        onClick={onClose}
      >
        Done
      </Button>
    </div>
  );
}

export default function PrivacyChoicesModal({
  isOpen,
  onClose
}: PrivacyChoicesModalProps) {
  const gpcEnabled = isGPCEnabled();
  // Toggle ON ("Allow") means the user is *not* opted out of sharing.
  const [allowSharing, setAllowSharing] = useState(true);
  const [isSaved, setIsSaved] = useState(false);

  // Re-read the persisted preference each time the modal opens so the toggle
  // reflects the current choice (handles the reopened / already-opted-out case).
  useEffect(() => {
    if (isOpen) {
      setAllowSharing(!isAdTrackingOptedOut());
      setIsSaved(false);
    }
  }, [isOpen]);

  // The MiniOverlayModal shell (react-modal) traps focus and closes on scrim
  // click, but does not bind Escape — wire it up here per the a11y spec.
  useEffect(() => {
    if (!isOpen) return undefined;

    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  function handleSave() {
    // Opt out when sharing is turned OFF ("Don't Allow").
    const optingOut = !allowSharing;
    const wasOptedOut = isAdTrackingOptedOut();

    // Fire analytics BEFORE flipping the preference: setAdTrackingOptOut
    // changes the consent state the RudderStack consent manager reads, so the
    // event must record against the pre-save state to avoid being dropped/raced
    // (the `analytics` consent category stays allowed even when opted out).
    // Only emit when the preference actually changes.
    if (optingOut && !wasOptedOut) {
      sendRudderstackEvent(ANALYTICS_EVENTS.AD_TRACKING_OPT_OUT_SET, {
        source: PRIVACY_CHOICES_SOURCE,
        gpc_enabled: isGPCEnabled()
      });
    } else if (!optingOut && wasOptedOut) {
      sendRudderstackEvent(ANALYTICS_EVENTS.AD_TRACKING_OPT_IN_RESTORED, {
        source: PRIVACY_CHOICES_SOURCE
      });
    }

    setAdTrackingOptOut(optingOut);
    setIsSaved(true);
  }

  const toggleLabel = allowSharing && !gpcEnabled ? 'On' : 'Off';
  const toggleSubtitle = gpcEnabled ? GPC_SUBTITLE : SHARING_SUBTITLE;

  return (
    <MiniOverlayModal
      maxWidth="600px"
      onCloseClick={onClose}
      closeButtonClassName="bg-white pv2 ph2 ba b--default br-100 mt4 mr4 z-99999"
      contentClassName="bg-white overflow-hidden"
      dataTestId="privacy-choices-modal"
    >
      {isSaved ? (
        <SavedConfirmation optedOut={!allowSharing} onClose={onClose} />
      ) : (
        <div className="PrivacyChoicesModal flex flex-column pv8 ph8 pv14-md ph16-md gap-24 md:gap-32">
          {/* pr5 keeps the title clear of the floating close button. */}
          <h2 className="font-serif md:font-32 font-24 fw-medium lh-title pr5">
            Your Privacy Choices
          </h2>

          <div className="secondary font-16 lh-body flex flex-column gap-16">
            <p>
              We use advertising partners like Meta (Facebook), Google, Bing,
              Pinterest, and LinkedIn to help us show relevant ads and
              understand site usage.
            </p>
            <p>
              You can choose not to share your activity with these partners. If
              you opt out, we won&apos;t load their tracking cookies or send
              them your information for advertising purposes. We&apos;ll
              remember your choice on this browser. We also honor Global Privacy
              Control (GPC) signals sent by your browser. Under the California
              Consumer Privacy Act, as amended by the California Privacy Rights
              Act, and similar state laws, this constitutes your right to opt
              out of the &apos;sale&apos; or &apos;sharing&apos; of your
              personal information.
              <br />
              <br />
              Learn more about our{' '}
              <a className="secondary underline" href={PRIVACY_PATH}>
                Privacy Policy
              </a>
              .
            </p>
          </div>

          <div className="flex justify-between items-center gap-16 pv4 ph5 bg-gray-100 ba b--gray-200 br12">
            <div className="flex flex-column">
              <span className="font-16 fw-medium">
                Allow advertising partners
              </span>
              <span className="secondary font-14 lh-body">
                {toggleSubtitle}
              </span>
            </div>
            <div className="flex items-center gap-8 flex-none">
              <Toggle
                checked={allowSharing}
                disabled={gpcEnabled}
                onChange={e => setAllowSharing(e.target.checked)}
                dataTestId="privacy-choices-toggle"
              />
              {/* minWidth fits the "Off" label so the toggle doesn't shift
                  when the text flips between On and Off. */}
              <span
                className={`font-14 tl ${gpcEnabled ? 'disabled' : 'secondary'}`}
                style={{ minWidth: '24px' }}
              >
                {toggleLabel}
              </span>
            </div>
          </div>

          {gpcEnabled && (
            <Banner color="blue" iconName="info" type="horizontal">
              Your browser&apos;s Global Privacy Control is enabled, so sharing
              is turned off and can&apos;t be changed here.
            </Banner>
          )}

          <Button
            variant="primary"
            size="default"
            className="w-100"
            disabled={gpcEnabled}
            onClick={handleSave}
          >
            Save my preferences
          </Button>
        </div>
      )}
    </MiniOverlayModal>
  );
}
